import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import MatchProfileCard from '../../components/recruiter/matching/MatchProfileCard';
import CandidateMatchTable from '../../components/recruiter/matching/CandidateMatchTable';
import EmailOutreachPanel from '../../components/recruiter/matching/EmailOutreachPanel';
import LinkedInScoutPanel from '../../components/recruiter/matching/LinkedInScoutPanel';
import { supabase } from '../../lib/supabaseClient';

/**
 * RFQMatches - Full page for RFQ candidate matching and outreach
 */
const RFQMatches = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);

  useEffect(() => {
    fetchRFQ();
  }, [id]);

  const fetchRFQ = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rfqError } = await supabase
        .from('rfqs')
        .select('*, client:clients(name)')
        .eq('id', id)
        .single();

      if (rfqError) throw rfqError;
      setRfq(data);
    } catch (err) {
      console.error('Error fetching RFQ:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !rfq) {
    return (
      <div className="text-center py-12">
        <Alert className="max-w-2xl mx-auto bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            Error loading RFQ: {error || 'Not found'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/recruiter/rfqs')} className="mt-4">
          Back to RFQs
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(`/recruiter/rfqs/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Find Candidate Matches</h1>
            <p className="text-muted-foreground mt-1">
              {rfq.ref_code} - {rfq.title}
            </p>
          </div>
        </div>
      </div>

      {/* Match Profile Card */}
      <MatchProfileCard rfqId={id} />

      {/* Candidate Match Table */}
      <CandidateMatchTable
        rfqId={id}
        onCandidatesSelected={setSelectedCandidateIds}
      />

      {/* Email Outreach Panel */}
      <EmailOutreachPanel
        rfqId={id}
        selectedCandidateIds={selectedCandidateIds}
        onEmailsSent={(emailJobs) => {
          console.log('Emails sent:', emailJobs);
          // Optionally navigate back or show success
        }}
      />

      {/* LinkedIn Scouting (read-only) */}
      <LinkedInScoutPanel rfqId={id} />
    </div>
  );
};

export default RFQMatches;
